import { useRouter } from 'vue-router';

/**
 * Class representing an application route.
 *
 * @example
 * // Using with <router-link>
 * <router-link :to="AppRoute.build(Route.LandingPage)"/>
 *
 * @example
 * // Using with router.push
 * router.push(AppRoute.build(Route.LandingPage));
 *
 * @example
 * // Using with next in navigation guards
 * next(AppRoute.build(Route.LandingPage));
 */
export default class AppRoute {

    /**
     * Creates an instance of AppRoute.
     *
     * @param {string} name
     * @param {Object} [params={}]
     * @param {Object} [options={}]
     */
    constructor(name, params = {}, options = {}) {
        this.name = name;
        this.params = params;
        for (const [k, v] of Object.entries(options) ?? {}) {
            this[k] = v;
        }
    }

    /**
     * Builds a new AppRoute instance.
     *
     * @param {string} name
     * @param {Object} [params={}]
     * @param {Object} [options={}]
     * @returns {AppRoute}
     */
    static build(name, params = {}, options = {}) {
        return new this(name, params, options);
    }

    /**
     * Computes and returns the resolved URL for the current route. The URL is generated based on the route name,
     * parameters, and additional properties defined in the current context.
     *
     * @return {string} The resolved URL as a string.
     */
    get url() {
        const $router = useRouter();
        const route = $router.resolve(this);
        return route.href;
    }

    /**
     * Resolves the current route using the provided router instance and returns the corresponding URL as a string.
     *
     * @param {import('vue-router').Router} router - The router instance used to resolve the current route.
     * @return {string} - The resolved URL of the current route.
     */
    getResolvedUrl(router) {
        return router.resolve(this)?.href;
    }
}
